import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { rideAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';
import StarRating from '../../components/StarRating';

const STATUS_CFG = {
  accepted:        { label: 'تم القبول', color: 'bg-blue-100 text-blue-700' },
  driver_en_route: { label: 'السائق في الطريق', color: 'bg-indigo-100 text-indigo-700' },
  arrived:         { label: 'وصل السائق', color: 'bg-purple-100 text-purple-700' },
  in_progress:     { label: 'المشوار جارٍ', color: 'bg-purple-100 text-purple-700' },
  completed:       { label: 'انتهى المشوار', color: 'bg-emerald-100 text-emerald-700' },
  cancelled:       { label: 'ملغى', color: 'bg-red-100 text-red-700' },
};

const EVENT_LABELS = {
  created: 'تم قبول العرض', driver_en_route: 'السائق في الطريق إليك', arrived: 'وصل السائق',
  in_progress: 'بدأ المشوار', completed: 'انتهى المشوار',
};

const RideOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const load = useCallback(() => {
    rideAPI.getOrderById(id)
      .then((res) => setOrder(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleSubmitReview = async () => {
    if (!ratingValue) { alert('يرجى اختيار تقييم'); return; }
    setSubmittingReview(true);
    try {
      await rideAPI.submitReview(id, { rating: ratingValue, comment: ratingComment || undefined });
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'تعذّر إرسال التقييم');
    } finally { setSubmittingReview(false); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let interval = setInterval(load, 8000);
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        load();
        interval = setInterval(load, 8000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);

  if (loading) return <div dir="rtl" className="min-h-screen bg-[#F5F9FF]"><AppNavbar /><p className="text-center py-16 text-gray-400">جاري التحميل...</p></div>;
  if (!order) return <div dir="rtl" className="min-h-screen bg-[#F5F9FF]"><AppNavbar /><p className="text-center py-16 text-gray-400">الطلب غير موجود</p></div>;

  const req = order.request || {};
  const st = STATUS_CFG[order.status] || { label: order.status, color: 'bg-gray-100 text-gray-600' };

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-16">
      <AppNavbar />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-black text-gray-800">{order.orderNumber}</h1>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
          </div>
          <p className="text-sm font-semibold text-gray-700 mb-1">{req.fromLabel} ← {req.toLabel}</p>
          <p className="text-lg font-black text-blue-700">{order.price} ج.س <span className="text-xs font-normal text-gray-400">(يُدفع نقداً للسائق)</span></p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
          <h2 className="font-bold text-gray-800 mb-2">🚕 بيانات السائق والمركبة</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-gray-500">السائق</p><p className="font-semibold text-gray-800">{order.driverName}</p></div>
            <div><p className="text-xs text-gray-500">الهاتف</p><p className="font-semibold text-gray-800" dir="ltr">{order.driverPhone}</p></div>
            {order.vehiclePlate && <div><p className="text-xs text-gray-500">رقم اللوحة</p><p className="font-semibold text-gray-800">{order.vehiclePlate}</p></div>}
          </div>
        </div>

        {order.status === 'completed' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-800 text-center font-bold mb-4">
            ✅ انتهى المشوار بنجاح
          </div>
        )}

        {order.status === 'completed' && (
          order.review ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4 text-center">
              <p className="text-xs text-gray-500 mb-2">تقييمك للسائق</p>
              <div className="flex justify-center"><StarRating value={order.review.rating} readOnly /></div>
              {order.review.comment && <p className="text-sm text-gray-600 mt-2">{order.review.comment}</p>}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
              <h2 className="font-bold text-gray-800 mb-3 text-center">قيّم رحلتك</h2>
              <div className="flex justify-center mb-3"><StarRating value={ratingValue} onChange={setRatingValue} /></div>
              <textarea rows={2} placeholder="تعليق (اختياري)" value={ratingComment} onChange={(e) => setRatingComment(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm mb-3 outline-none focus:border-[#1E3A8A]" />
              <button onClick={handleSubmitReview} disabled={submittingReview || !ratingValue}
                className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition-all">
                {submittingReview ? '...جارٍ الإرسال' : 'إرسال التقييم'}
              </button>
            </div>
          )
        )}

        {order.events?.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-bold text-gray-600 mb-2">سجل المشوار</p>
            <div className="space-y-2">
              {order.events.map((ev) => (
                <div key={ev.id} className="flex items-center gap-3 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                  <span className="text-gray-700 font-semibold">{EVENT_LABELS[ev.event] || ev.event}</span>
                  <span className="text-gray-400">{new Date(ev.createdAt).toLocaleString('ar-SA')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default RideOrderDetailPage;
